import { useCallback, useEffect, useRef, useState } from "react";
import { TriggeredHook } from "sonobello.utilities.react";

import { LeadResult } from "../../../../Models/Lead";
import CreateContactResultRequest, { CreateContactResultRequestPayload } from "../Types/CreateContactResultRequest";
import UpdateContactResultResponse from "../Types/CreateOrUpdateContactResultResponse";
import ILegacyCreateOrUpdateContactResultRequest from "../Types/ILegacyCreateOrUpdateContactResultRequest";

type DelegateHook = TriggeredHook<UpdateContactResultResponse, CreateContactResultRequest>;

/**
 * A decorator for the {@link DelegateHook} that provides translation between legacy objects and naming
 * conventions to the latest {@link CreateContactResultRequest} and {@link UpdateContactResultResponse} patterns
 * expected by the business.
 */
const BuildUsePostLeadResultLegacyWrapper: (
  useDelegate: DelegateHook
) => TriggeredHook<LeadResult, ILegacyCreateOrUpdateContactResultRequest, ILegacyCreateOrUpdateContactResultRequest> =
  useDelegate => () => {
    const [wrapperResponse, setWrapperResponse] = useState<LeadResult | null>(null);

    const { execute, result, isLoading: isDelegateLoading, error } = useDelegate();
    const requestRef = useRef<ILegacyCreateOrUpdateContactResultRequest | null>(null);

    const wrapperExecute = useCallback((request: ILegacyCreateOrUpdateContactResultRequest) => {
      requestRef.current = request;
      execute(
        new CreateContactResultRequest(
          request.leadId,
          new CreateContactResultRequestPayload(
            request.leadResult.type,
            request.leadResult.customerId,
            request.leadResult.appointmentId,
            request.leadResult.opportunityId,
            Object.entries(request.leadResult.disqualifyingReasons)
              .filter(([, isSelected]) => isSelected)
              .map(([id]) => id),
            Object.entries(request.leadResult.notOfferedProcedures)
              .filter(([, isSelected]) => isSelected)
              .map(([id]) => id),
            request.leadResult.notes,
            request.shouldDeleteOpportunity
          )
        )
      );
    }, []);

    useEffect(() => {
      if (!result || !requestRef.current) return;

      setWrapperResponse({
        id: result.id,
        type: result.typeId,
        customerId: result.patientId,
        appointmentId: result.appointmentId,
        opportunityId: result.leadId,
        disqualifyingReasons: requestRef.current.leadResult.disqualifyingReasons,
        notOfferedProcedures: requestRef.current.leadResult.notOfferedProcedures,
        notes: requestRef.current.leadResult.notes
      });
    }, [result]);

    return {
      execute: wrapperExecute,
      result: wrapperResponse,
      error: error ? requestRef.current : null,
      isLoading: isDelegateLoading
    };
  };

export default BuildUsePostLeadResultLegacyWrapper;
